/**
 * The ten upload slots on Upload Data — what each tile offers and what it says.
 *
 * Moved out of `Intake.tsx` (2026-09-28, stage 4a) so a test can hold every
 * tile to the service rather than to itself: `tests/slot-claims.test.ts`
 * checks each tile's `accept` against the formats intake admits, the tile
 * keys against the slots the service knows, and each tile's words against its
 * own `accept` — all read from `vocabularies.json`, never re-typed.
 *
 * The labels are the legacy's. The accept lists were too, and that is what was
 * wrong with them: the legacy's document list offered `.tif .tiff .bmp .webp
 * .docx`, and this service refuses all five at intake (`intake.py`
 * `SUPPORTED_EXTENSIONS`, `INTAKE_QUARANTINES`). A tile offering a format the
 * server refuses is a promise the next screen breaks.
 *
 * `direction` drives the pre-upload guard; only the two invoice slots have one,
 * as in the legacy — everywhere else a filename is not evidence of anything.
 * `pending` names what a slot does NOT do, shown on the card rather than hidden.
 */

export const SPREADSHEETS = '.xlsx,.xls,.csv'
export const DOCS = '.pdf,.jpg,.jpeg,.png'

export interface Slot {
  key: string
  label: string
  hint: string
  accept: string
  direction?: 'sales' | 'purchase'
  pending?: string
}

export const SLOTS: Slot[] = [
  {
    key: 'main_data',
    label: 'Main GST Data File',
    hint: 'TB, Sales Inv Ledger, PH SALE, RR GST, RCM REG (for full return)',
    accept: SPREADSHEETS,
  },
  {
    key: 'portal_gstr1',
    label: 'Portal GSTR-1 (SectionWiseSummary)',
    hint: 'From GST Portal — the SectionWiseSummary workbook, or the per-table CSVs',
    accept: SPREADSHEETS,
    pending:
      'Read as the client’s own GSTR-1 (Tally’s HSN/SAC summary or GSTN’s Excel layout), checked against its own totals and compared with ProGST’s return on the GSTR-1 screen; kept as reference, never booked as sales',
  },
  {
    key: 'filed_gstr1',
    label: 'Filed GSTR-1 (Portal)',
    // C1 and C2 (2026-09-28). This tile offered `.json` first — the one format
    // the service refuses here — and promised the file would be "reconciled vs
    // what this app prepared". Nothing reads a workbook from this slot. The
    // comparison exists, on Prepared vs Filed, and it reads the portal's
    // filed-return `.json`, given to that screen directly.
    hint: 'The SectionWiseSummary workbook, kept with the period for the record',
    accept: SPREADSHEETS,
    pending: 'Stored only — to compare, give the portal’s filed-return .json to Prepared vs Filed',
  },
  {
    key: 'sales_report',
    label: 'SalesReport (Retail POS)',
    hint: 'Store POS SalesReport Excel/CSV — all stores',
    accept: SPREADSHEETS,
  },
  {
    key: 'sales_invoices',
    label: 'Sales Invoices / B2B',
    // Spreadsheets are refused here in the legacy too: a register dropped into
    // the invoice slot parses as one enormous invoice. And not "Word": a .docx
    // is quarantined at intake with "convert to PDF and re-upload".
    hint: 'Individual B2B invoices — PDF or image (PNG/JPG)',
    accept: DOCS,
    direction: 'sales',
  },
  {
    key: 'purchase_invoices',
    label: 'Purchase Invoices',
    // Was "any format", beside an accept list that already was not.
    hint: 'Purchase register, GRN — Excel/CSV, PDF or image',
    accept: `${SPREADSHEETS},${DOCS}`,
    direction: 'purchase',
  },
  {
    key: 'gstr2b',
    label: 'GSTR-2B (Auto-drafted ITC)',
    hint: 'Portal Excel — caps claimable ITC',
    accept: SPREADSHEETS,
    // NOT "as JSON", and not "for now": the Reconciliation screen has
    // imported the portal's own .xlsx since ADR-0030, and this is where the
    // 2B belongs permanently. Dropping one here stores the file and
    // reconciles nothing, which is what the sentence has to say — and it
    // names the file, because Reconciliation's picker does not take a .csv.
    pending: 'Stored, not reconciled — import the portal’s .xlsx on the Reconciliation screen',
  },
  {
    key: 'gstr2a',
    label: 'GSTR-2A (Dynamic)',
    hint: 'Portal Excel — supplier-filed invoices',
    accept: SPREADSHEETS,
    // FR-E28 (C-13): the Reconciliation screen reads a 2A now — its *2A vs 2B*
    // tab — and stores it beside the 2B; it is claimed from by nothing. As with
    // the 2B tile, dropping the file HERE stores it and reads nothing, which
    // the sentence has to say; and it names the file, because the screen's
    // picker does not take a .csv.
    pending:
      'Stored, not read — import the portal’s .xlsx on the Reconciliation screen (Import 2A); it is compared with the 2B and never claimed from',
  },
  {
    key: 'credit_debit_notes',
    label: 'Credit / Debit Notes',
    hint: 'Sales returns, credit memos',
    accept: `${SPREADSHEETS},${DOCS}`,
  },
  {
    // FR-E11 (e2e-filing B-04): Table 11. A grid of advances received and adjusted — date,
    // place of supply, rate, amount, and a kind column — bound in the column dialog like a
    // sales register. Spreadsheets only: Table 11 is a summary per state and rate, and a
    // receipt voucher as a document has no lane.
    key: 'advances',
    label: 'Advances (Table 11)',
    hint: 'Advances received and adjusted — Excel/CSV with date, place of supply, rate, amount, kind',
    accept: SPREADSHEETS,
    direction: 'sales',
  },
  {
    key: 'other_files',
    label: 'Other Supporting Files',
    hint: 'E-way bills, HSN master, etc.',
    accept: `${SPREADSHEETS},${DOCS}`,
  },
]
